import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Modal, RadioGroup, Radio, Switch, Box, Button, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import '../translations.js';
// Reuse the website rescraping modal styling so the two look identical.
import './rescrapingSettingsModal.css';
import InfoTooltip from './InfoTooltip.js';

/**
 * Auto-sync settings for connected sources (Google Drive / OneDrive / Notion).
 * Mirrors RescrapingSettingsModal but is connector-focused: no per-link
 * selection and no "scan for new links" (connectors auto-include every
 * imported file). It edits the SAME bot rescraping schedule as the website
 * rescraping modal — one auto-sync cadence per bot.
 */
const PROVIDER_LABELS = { google_drive: 'Google Drive', onedrive: 'OneDrive', notion: 'Notion' };

const ConnectorSyncSettingsModal = ({ open, onClose, onSave, bot, provider, config, onOpenSubscriptionModal }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [frequency, setFrequency] = useState(config?.interval ?? 'monthly');
  const [fetchNew, setFetchNew] = useState(config?.fetch_new ?? false);
  const [removeOldLinks, setRemoveOldLinks] = useState(config?.remove_missing ?? false);

  useEffect(() => {
    if (!open) return;
    setFrequency(config?.interval ?? 'monthly');
    setFetchNew(config?.fetch_new ?? false);
    setRemoveOldLinks(config?.remove_missing ?? false);
  }, [open, config]);

  const handleSave = () => {
    onSave({ frequency, fetchNew, removeOldLinks });
    onClose();
  };

  if (!open) return null;

  const plan = bot?.subscription_plan?.name;
  const hasAccess = plan === "Boost" || plan === "Ultimo";
  const providerLabel = PROVIDER_LABELS[provider] || '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", outline: 'none' }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "20px",
          width: "100%",
          maxWidth: isMobile ? "80%" : "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          outline: 'none',
          position: 'relative',
        }}
      >
        {!hasAccess ? (
          <>
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              aria-label="close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
            <div className="rescraping-upgrade-modal-content">
              <div className="rescraping-upgrade-modal-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="rescraping-upgrade-modal-title">{t('premium_feature')}</h2>
              <p className="rescraping-upgrade-modal-description">
                {t('connector_sync_upgrade_description')}
              </p>
              <div className="rescraping-upgrade-plan-info">
                <div className="rescraping-upgrade-plan-badge rescraping-upgrade-current-plan">
                  <span className="rescraping-upgrade-plan-label">{t('current_plan')}</span>
                  <span className="rescraping-upgrade-plan-name">{bot?.subscription_plan?.name || t('plan_free')}</span>
                </div>
                <div className="rescraping-upgrade-arrow-icon">→</div>
                <div className="rescraping-upgrade-plan-badge rescraping-upgrade-required-plan">
                  <span className="rescraping-upgrade-plan-label">{t('skill_required')}</span>
                  <span className="rescraping-upgrade-plan-name">Boost+</span>
                </div>
              </div>
              <button className="rescraping-upgrade-button" onClick={() => onOpenSubscriptionModal(bot)}>
                {t('upgrade_now')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="rs-title">{providerLabel ? t('connector_sync_settings_with_provider', { provider: providerLabel }) : t('connector_sync_settings')}</h2>
            <p className="rs-connector-sync-intro">{t('connector_sync_intro')}</p>

            {/* frequency */}
            <div className="rs-section">
              <span className="rs-label">
                {t('rescraping_frequency')}:
                <InfoTooltip text={t('connector_sync_frequency_tooltip')} />
              </span>

              <RadioGroup
                row
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="rs-radio-group"
                sx={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '0.5rem' }}
              >
                {/* Monthly */}
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Radio
                    value="monthly"
                    sx={{
                      mr: -0.5,
                      color: plan === 'Ultimo' ? '#5616ea' : '#9e9e9e',
                      '& .MuiSvgIcon-root': { transition: 'color 0.2s ease', color: plan === 'Ultimo' ? '#5616ea' : '#b0b0b0' },
                      '&.Mui-checked .MuiSvgIcon-root': { color: '#5616ea' },
                    }}
                  />
                  <span style={{ fontSize: '0.95rem', color: '#333', cursor: 'pointer' }} onClick={() => setFrequency('monthly')}>
                    {t('monthly')}
                  </span>
                </Box>

                {/* Daily (Ultimo only) */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.8}
                    sx={{ cursor: plan === 'Ultimo' ? 'pointer' : 'not-allowed', opacity: plan === 'Ultimo' ? 1 : 0.8 }}
                  >
                    <Radio
                      value="daily"
                      disabled={plan !== 'Ultimo'}
                      sx={{
                        mr: -0.5,
                        color: plan === 'Ultimo' ? '#5616ea' : '#bdbdbd',
                        '& .MuiSvgIcon-root': { transition: 'color 0.2s ease', color: plan === 'Ultimo' ? '#5616ea' : '#d0d0d0' },
                        '&.Mui-checked .MuiSvgIcon-root': { color: '#5616ea' },
                        '&.Mui-disabled .MuiSvgIcon-root': { color: '#cfcfcf' },
                      }}
                    />
                    <span
                      style={{ fontSize: '0.95rem', color: plan === 'Ultimo' ? '#333' : '#777', cursor: plan === 'Ultimo' ? 'pointer' : 'not-allowed' }}
                      onClick={() => plan === 'Ultimo' && setFrequency('daily')}
                    >
                      {t('daily')}
                    </span>
                  </Box>

                  {plan !== 'Ultimo' && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ backgroundColor: '#f9f9ff', border: '1px solid #e0e0f8', borderRadius: '8px', padding: '4px 10px', marginLeft: '0.4rem' }}
                    >
                      <span style={{ fontSize: '0.85rem', color: '#5616ea', fontWeight: 500 }}>
                        {t('ultimo_plan_required')}
                      </span>
                      <Button
                        size="small"
                        onClick={() => onOpenSubscriptionModal(bot)}
                        sx={{
                          textTransform: 'none', fontSize: '0.8rem', padding: '2px 8px', minWidth: 'auto',
                          color: '#ffffff', backgroundColor: '#5616ea', borderRadius: '6px', boxShadow: 'none',
                          '&:hover': { backgroundColor: '#4a14d6' },
                        }}
                      >
                        {t('upgrade')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </RadioGroup>
            </div>

            {/* include new files — OneDrive folders / Notion databases only.
                Google Drive's drive.file scope can't enumerate container contents. */}
            {provider !== 'google_drive' && (
              <div className="rs-section rs-switch-row">
                <span className="rs-label-new-links">
                  {t('connector_fetch_new')}:
                  <InfoTooltip text={t('connector_fetch_new_tooltip')} />
                </span>
                <div className="new-links-text-toggle">
                  <Switch
                    checked={fetchNew}
                    onChange={(e) => setFetchNew(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                      '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                    }}
                  />
                  <span className="rs-switch-text">{t('connector_fetch_new_label')}</span>
                </div>
              </div>
            )}

            {/* remove missing files */}
            <div className="rs-section rs-switch-row">
              <span className="rs-label-new-links">
                {t('connector_remove_missing')}:
                <InfoTooltip text={t('connector_remove_missing_tooltip')} />
              </span>
              <div className="new-links-text-toggle">
                <Switch
                  checked={removeOldLinks}
                  onChange={(e) => setRemoveOldLinks(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                    '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                  }}
                />
                <span className="rs-switch-text">{t('connector_remove_missing_label')}</span>
              </div>
              {removeOldLinks && (
                <div className="rs-warning-box">{t('connector_remove_missing_warning')}</div>
              )}
            </div>

            {/* buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} marginTop={2}>
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{ color: "#5616ea", borderColor: "#5616ea", "&:hover": { borderColor: "#5e0fb3", backgroundColor: "#f3eaff" } }}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{ backgroundColor: "#5616ea", "&:hover": { backgroundColor: "#4a14d6" } }}
              >
                {t("save")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ConnectorSyncSettingsModal;
